import {
    FormControlLabel,
    Checkbox,
    FormGroup,
    Grid,
    FormHelperText,
} from '../../base-components';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { ReactNode } from 'react';

export interface ICheckboxItem {
    key: string;
    label: string;
    description?: string;
    disabled?: boolean;
    additionalContent?: ReactNode;
}

interface ICheckboxGroupProps<F extends FieldValues> {
    name: Path<F>;
    control: Control<F>;
    options: Array<ICheckboxItem>;
    disabled?: boolean;
    spacing?: number;
    dataTestId?: string;
    direction?: 'row' | 'column';
    size?: 'small' | 'medium';
}

const FormCheckboxGroup = <F extends FieldValues>(
    props: ICheckboxGroupProps<F>
) => {
    const {
        name,
        options,
        spacing = 0,
        dataTestId,
        disabled,
        control,
        direction = 'column',
        size = 'medium',
    } = props;

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => {
                const selectedValues: Array<string | number> = Array.isArray(field.value)
                    ? field.value
                    : [];

                const handleChange = (key: string | number, checked: boolean) => {
                    if (checked) {
                        field.onChange([...selectedValues, key]);
                    } else {
                        field.onChange(selectedValues.filter((v) => v !== key));
                    }
                };

                return (
                    <FormGroup data-testid={dataTestId || name}>
                        <Grid container spacing={spacing} direction={direction}>
                            {options.map((item: ICheckboxItem) => {
                                const isChecked = selectedValues.includes(item.key) ||
                                    selectedValues.includes(Number(item.key));

                                return (
                                    <Grid
                                        key={item.key}
                                        item
                                        xs={direction === 'column' ? 12 : 'auto'}
                                    >
                                        <FormControlLabel
                                            sx={{ width: '100%' }}
                                            label={item.label}
                                            disabled={item.disabled || disabled}
                                            data-testid={`FormCheckboxGroup-item-${name}-${item.key}`}
                                            control={
                                                <Checkbox
                                                    size={size}
                                                    disabled={item.disabled || disabled}
                                                    checked={isChecked}
                                                    onChange={(e) =>
                                                        handleChange(item.key, e.target.checked)
                                                    }
                                                />
                                            }
                                        />
                                        {isChecked && item.additionalContent}
                                    </Grid>
                                );
                            })}
                        </Grid>
                        {error?.message && (
                            <FormHelperText error>{error.message}</FormHelperText>
                        )}
                    </FormGroup>
                );
            }}
        />
    );
};

export default FormCheckboxGroup;
